import { useEffect, useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import CustomLink from "@Components/CustomLink";
import styles from "./radioChip.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";

type RadioChipProps = {
  name: string;
  label: string | React.ReactNode;
  value: string;
  isSelected: boolean;
  setSelectedValue: any;
  asLink?: boolean;
  /** SEO (PLG-3406): render the link without `href` until mounted, so it is absent from SSR HTML. */
  hideHrefUntilMounted?: boolean;
};

const RadioChip = ({
  name,
  label,
  value,
  isSelected,
  setSelectedValue,
  asLink = false,
  hideHrefUntilMounted = false,
}: RadioChipProps) => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const handleClick = (e: any) => {
    e.preventDefault();
    setSelectedValue(value);
  };

  const handleRadioChange = (e: any) => {
    e.stopPropagation();
    setSelectedValue(value);
  };

  const { t } = useTranslationHook();
  const onKeyDown = (event: any) => {
    if (event.key === "Enter") {
      handleClick(event);
    }
  };

  if (asLink)
    return (
      <CustomLink
        className={formatClassNames(
          styles.radioContainer,
          styles.radioContainerAsLink,
          isSelected && styles.radioContainer_selected,
        )}
        onClick={(e: any) => handleClick(e)}
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDown}
        href={hideHrefUntilMounted && !isMounted ? undefined : value}
        isExternal
      >
        <input
          type="radio"
          name={t(name)}
          id={value}
          checked={isSelected}
          onChange={handleRadioChange}
          onClick={(e) => e.stopPropagation()}
        />
        <span>{label ?? value}</span>
      </CustomLink>
    );

  return (
    <div
      className={formatClassNames(
        styles.radioContainer,
        isSelected && styles.radioContainer_selected,
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <input
        type="radio"
        name={t(name)}
        id={value}
        checked={isSelected}
        onChange={handleRadioChange}
        onClick={(e) => e.stopPropagation()}
      />
      <span>{label ?? value}</span>
    </div>
  );
};

export default wrapperHOC(RadioChip, {
  componentName: "RadioChip-FilterSubsection-SearchListDesktop-SearchPage",
  showForChina: true,
});
